<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { useFeatures } from '@/composables/useFeatures';
import {
  LayoutDashboard,
  Crosshair,
  Grid3x3,
  Workflow,
  Settings2,
  FileText,
  FileCode,
  Flag,
  Settings,
  ShieldAlert,
  Building,
  Users,
  Target,
  Fingerprint,
  Upload,
  Inbox,
  Grid2x2Check,
  Activity,
} from 'lucide-vue-next';

interface Props {
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
});

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  route: string;
}

const baseNavItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, route: '/' },
  { label: 'MITRE Matrix', icon: Grid3x3, route: '/mitre-matrix' },
  { label: 'Attack Chain', icon: Workflow, route: '/attack-chain' },
  { label: 'Detection Rules', icon: Settings2, route: '/detection-rules' },
  { label: 'Reports', icon: FileText, route: '/reports' },
];

// Campaigns/Indicators are gated by the cross-pivots flag — they only
// make sense once pivoting between artifacts is turned on.
const pivotNavItems: NavItem[] = [
  { label: 'Campaigns', icon: Flag, route: '/campaigns' },
  { label: 'Indicators', icon: Fingerprint, route: '/indicators' },
];

const baseAdminItems: NavItem[] = [
  { label: 'Threat Actors', icon: ShieldAlert, route: '/admin/threats' },
  { label: 'Campaigns', icon: Crosshair, route: '/admin/campaigns' },
  { label: 'Indicators', icon: Fingerprint, route: '/admin/indicators' },
  { label: 'Detection Rules', icon: FileCode, route: '/admin/detection-rules' },
  { label: 'Reports', icon: FileText, route: '/admin/reports' },
  { label: 'Customers', icon: Building, route: '/admin/customers' },
  { label: 'Users', icon: Users, route: '/admin/users' },
  { label: 'Rankings', icon: Target, route: '/admin/rankings' },
  { label: 'MITRE', icon: Grid2x2Check, route: '/admin/mitre' },
  { label: 'Scoring', icon: Activity, route: '/admin/scoring' },
];

// Collaboration Inbox nav item — CWR-staff-only, gated on collabLoop flag.
const collabAdminItem: NavItem = {
  label: 'Collab Inbox',
  icon: Inbox,
  route: '/admin/collab',
};

const utilityItems: NavItem[] = [
  { label: 'Settings', icon: Settings, route: '/settings' },
];

const isSuperAdmin = computed(() => authStore.user?.role === 'super_admin');

// E15: Import nav item — only shows for users who can actually create
// content (analyst/admin/super_admin) AND only when the feature flag is on.
const { flags } = useFeatures();

// E16: merge cross-pivot items into the main nav when the flag is on.
const navItems = computed<NavItem[]>(() => {
  if (flags.value.crossPivots) {
    // Insert Campaigns + Indicators between Attack Chain and Detection Rules.
    return [
      baseNavItems[0],
      baseNavItems[1],
      baseNavItems[2],
      ...pivotNavItems,
      baseNavItems[3],
      baseNavItems[4],
    ];
  }
  return baseNavItems;
});
const canImport = computed(() => {
  if (!flags.value.bulkImport) return false;
  const role = authStore.user?.role;
  return role === 'analyst' || role === 'admin' || role === 'super_admin';
});

// E21: Collab Inbox slides into the admin list when the flag is on.
const adminItems = computed<NavItem[]>(() => {
  if (flags.value.collabLoop) {
    return [...baseAdminItems, collabAdminItem];
  }
  return baseAdminItems;
});

function isActive(routePath: string): boolean {
  if (routePath === '/') return route.path === '/';
  return route.path.startsWith(routePath);
}

function navigate(routePath: string) {
  router.push(routePath);
}
</script>

<template>
  <nav
    class="sidenav flex flex-col gap-0 border-r border-border-default bg-surface-900 cwr-motion-width overflow-hidden"
    :class="collapsed ? 'w-16' : 'w-60'"
  >
    <!-- Main navigation items -->
    <div class="sidenav-main flex-1 overflow-y-auto flex flex-col gap-0.5 p-2">
      <button
        v-for="item in navItems"
        :key="item.route"
        class="nav-item flex items-center gap-3 rounded-md text-sm font-medium cwr-motion-all"
        :class="[
          isActive(item.route)
            ? 'bg-surface-700 text-text-primary border-l-2 border-l-accent-blue'
            : 'text-text-muted hover:bg-surface-700 hover:text-text-secondary',
          collapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2',
        ]"
        :title="collapsed ? item.label : undefined"
        @click="navigate(item.route)"
      >
        <component :is="item.icon" class="w-5 h-5 shrink-0" />
        <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
      </button>
    </div>

    <!-- Admin items (super_admin only) -->
    <div v-if="isSuperAdmin" class="sidenav-admin flex flex-col gap-0.5 p-2 border-t border-border-default">
      <span v-if="!collapsed" class="text-[10px] uppercase tracking-wider text-text-muted px-3 py-1">Admin</span>
      <button
        v-for="item in adminItems"
        :key="item.route"
        class="nav-item flex items-center gap-3 rounded-md text-sm font-medium cwr-motion-all"
        :class="[
          isActive(item.route)
            ? 'bg-surface-700 text-text-primary border-l-2 border-l-accent-blue'
            : 'text-text-muted hover:bg-surface-700 hover:text-text-secondary',
          collapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2',
        ]"
        :title="collapsed ? item.label : undefined"
        @click="navigate(item.route)"
      >
        <component :is="item.icon" class="w-5 h-5 shrink-0" />
        <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
      </button>
    </div>

    <!-- Import (E15) — feature-flagged, role-gated -->
    <div
      v-if="canImport"
      class="sidenav-workflow flex flex-col gap-0.5 p-2 border-t border-border-default"
      data-qa-nav-workflow
    >
      <button
        class="nav-item flex items-center gap-3 rounded-md text-sm font-medium cwr-motion-all"
        :class="[
          isActive('/import')
            ? 'bg-surface-700 text-text-primary border-l-2 border-l-accent-blue'
            : 'text-text-muted hover:bg-surface-700 hover:text-text-secondary',
          collapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2',
        ]"
        :title="collapsed ? 'Import' : undefined"
        data-qa-nav-import
        @click="navigate('/import')"
      >
        <Upload class="w-5 h-5 shrink-0" />
        <span v-if="!collapsed" class="truncate">Import</span>
      </button>
    </div>

    <!-- Utility items (pinned bottom) -->
    <div class="sidenav-utility flex flex-col gap-0.5 p-2 border-t border-border-default">
      <button
        v-for="item in utilityItems"
        :key="item.route"
        class="nav-item flex items-center gap-3 rounded-md text-sm font-medium cwr-motion-all"
        :class="[
          isActive(item.route)
            ? 'bg-surface-700 text-text-primary border-l-2 border-l-accent-blue'
            : 'text-text-muted hover:bg-surface-700 hover:text-text-secondary',
          collapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2',
        ]"
        :title="collapsed ? item.label : undefined"
        @click="navigate(item.route)"
      >
        <component :is="item.icon" class="w-5 h-5 shrink-0" />
        <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
      </button>
    </div>
  </nav>
</template>
